import type { GenericError, RequestStatus } from '@skynet/data-ws-core';
import type { AssetType, PositionType } from '@skynet/types-asset-type';
import type { PricesItem, PricesList } from '@skynet/types-price-item';
import type { JsonBalanceExtension, JsonBalanceGroupBase } from 'src/store/shared/balances/types';

export const historyItemTypeMap: Record<string, string> = {
  0: 'quote',
  1: 'base',
  2: 'other',
};

export type accountId = string;
export type date = string;
export type BalanceHistoryMap = Record<accountId, Record<date, JsonBalanceHistoryData[]>>;

export type InitialBalancesMap = Record<accountId, Record<date, InitialBalanceItem[]>>;
export type InitialValuesMap = Record<accountId, Record<string, InitialBalanceItem>>;

export interface InitialBalanceItem {
  token: string;
  value: number;
  addedBy: string;
}

export interface InitialBalanceItemExtended extends InitialBalanceItem {
  date: string;
}

export enum BalanceHistoryCommands {
  BalanceHistory = 'BalanceHistory',
  GetBalanceHistory = 'GetBalanceHistory',
  SetInitialBalance = 'SetInitialBalance',
  PriceHistory = 'PricesHistory',
  Prices = 'Prices',
  ResetInitialBalance = 'ResetInitialBalance',
}

export interface PayloadGetBalanceHistory {
  projectID?: number;
  loanID?: number;
  startDate: string;
  endDate: string;
}

export interface PriceHistoryItemType {
  /** UTC wall-clock "YYYY-MM-DD HH:mm", like every point of the series. */
  date: string;
  price: number;
  /**
   * Only on the live point a "current" series ends on, so a price-less live
   * balance (a `JsonBalanceHistoryItem` with `isCurrent`) pairs with this quote
   * whatever either is dated (SKY-4575).
   */
  isCurrent?: boolean;
}

export interface PriceHistoryItem {
  price: number;
  token: string;
}

export interface SetInitialBalancePayload {
  date?: string; // YYYY-MM-DD
  accountID: number | string;
  token: string;
  amount: number; // send Number.MIN_SAFE_INTEGER (-9007199254740991) to reset initial balance to undefined
}

export interface ResetInitialBalancePayload {
  date: string; // YYYY-MM-DD
  accountID: number | string;
  token: string;
}

export type SetInitialBalanceFormValues = SetInitialBalancePayload & {
  type: 'base' | 'quote' | 'other';
};

export interface SetInitialBalanceModal {
  balance: JsonBalanceHistoryExtended;
  foundAsset: JsonBalanceHistoryAsset[];
  isStart: boolean;
}

export type SetObjInitialBalanceFormValues = Record<string, SetInitialBalanceFormValues>;

export interface JsonBalanceHistoryData {
  assetType?: AssetType;
  positionType?: PositionType | null;
  entryPrice: number;
  lastPrice: number;
  leverage: number;
  marginBalance: number;
  priceDetails?: string;
  quotePrice: number;
  token: string;
  type: number; // 0 - quote, 1 - base, 2 - other
  unrealizedPnL: number;
  volume: number;
}

export interface JsonBalanceHistoryItem extends JsonBalanceHistoryData {
  date: string; // YYYY-MM-DD
  accountID: string;
  /** Only on the synthetic live snapshot built from the current balances. */
  isCurrent?: boolean;
}

export interface JsonBalanceHistoryItemExtended {
  accountID: string;
  date: string;
  type: number;
  token: string;
  price: number;
  quotePrice: number;
  historyAmount: number;
  initialAmount: number;
}

export interface JsonBalanceHistorySuccess {
  command: BalanceHistoryCommands.BalanceHistory;
  id: number;
  regular: BalanceHistoryMap;
  overrides: BalanceHistoryMap;
  initialBalances: InitialBalancesMap;
}

export interface JsonPriceHistorySuccess {
  command: BalanceHistoryCommands.PriceHistory;
  id: number;
  list: PricesList;
}

export interface JsonPricesSuccess {
  command: BalanceHistoryCommands.Prices;
  id: number;
  list: PricesList;
}

export interface BalanceHistoryState {
  requestStatus: RequestStatus;
  error: null | GenericError;

  regular: BalanceHistoryMap;
  overrides: BalanceHistoryMap;
  initialBalances: InitialBalancesMap;

  startDate?: string; // YYYY-MM-DD
  startTime?: string; // HH:mm
  endDate?: string; // YYYY-MM-DD
  endTime?: string; // HH:mm | 'current'

  // set initial balance
  isInitialBalanceModalOpen: boolean;
  formSetInitialBalance?: SetObjInitialBalanceFormValues;
  resSetInitialBalance: RequestStatus;
  priceHistory: Record<string, PricesItem[]>;
  prices: Record<string, PricesItem[]>;
}

export interface HistoryDataItem {
  price: number;
  volume: number;
  volumeUSD: number;
  volumeHint?: string;
  volumeUSDHint?: string;
  isSmallBalance: boolean;
}

export interface UniqueTokensList {
  token: string;
  type: number;
  isSmallBalance?: boolean;
}

export interface NewComparisonItem {
  startDate: string;
  endDate: string;
  startData: HistoryDataItem | null;
  startHistoryItem: JsonBalanceHistoryItem | undefined;
  endData: HistoryDataItem | null;
  endHistoryItem: JsonBalanceHistoryItem | undefined;
  diff: HistoryDataItem | null;
  isSmallBalance: boolean;
}

export interface JsonBalanceHistoryAsset {
  token: string;
  lastPrice: number;
  leverage: number;
  // extras for comparison
  comparison: NewComparisonItem;
  history: JsonBalanceHistoryItem[];
  isDoubling?: boolean;
  isSmallBalance?: boolean;
  type: string; // 'quote' | 'base' | 'other'
  /**
   * Marks the asset as a derivative (Position) or spot (Spot).
   * Used for summary assets where history items are not available.
   */
  assetType?: AssetType;
}

export type JsonBalanceHistoryExtended = JsonBalanceExtension & {
  // from JsonBalance
  accountID: string;
  accountName: string;
  isWallet: boolean;
  assets: JsonBalanceHistoryAsset[];
  exchangeID: number;
  exchangeName?: string;
  updateTime?: string;
  updated?: string;
  /**
   * Dates of this account's balance snapshots — backend ones plus the live
   * "current" one — i.e. the moments its whole balance is known. Unlike asset
   * history, excludes single-token points synthesised from transactions.
   */
  snapshotDates?: string[];
};

export type JsonBalanceHistoryExtendedList = JsonBalanceHistoryExtended &
  JsonBalanceGroupBase & {
    groupAccountsId: string[];
  };

export type JsonBalanceHistoryGroup = JsonBalanceGroupBase & {
  assets: JsonBalanceHistoryAsset[];
  children: JsonBalanceHistoryExtended[];
};

export type BalanceHistoryTableRow = JsonBalanceHistoryExtended | JsonBalanceHistoryGroup;
